import React, { useState, useRef, useEffect } from 'react';
import { Columns } from 'lucide-react';
import { TableColumn } from '@app-types';
import Button from '@components/common/Button';
import { Checkbox } from '@components/common';
import styles from './ColumnSelector.module.css';

interface ColumnSelectorProps {
  columns: TableColumn[];
  visibleColumns: string[];
  onToggleColumn: (columnKey: string) => void;
  dataTestId?: string;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleColumns,
  onToggleColumn,
  dataTestId = 'column-selector'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isColumnVisible = (columnKey: string): boolean => {
    return visibleColumns.includes(columnKey);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.columnSelector} ref={dropdownRef} data-test-id={dataTestId}>
      <Button variant="ghost" size="sm" onClick={toggleDropdown} dataTestId={`${dataTestId}-toggle-button`}>
        <Columns size={16} className={styles.icon} />
        Columns
      </Button>

      {isOpen && (
        <div className={styles.columnDropdown} data-test-id={`${dataTestId}-dropdown`}>
          <div className={styles.columnDropdownHeader}>
            <span>Show/Hide Columns</span>
          </div>
          <div className={styles.columnDropdownContent}>
            {columns
              .filter((col) => !col.hiddenFromSelector)
              .map((column) => (
                <div key={column.key} className={styles.columnOption}>
                  <Checkbox
                    checked={isColumnVisible(column.key)}
                    onChange={() => onToggleColumn(column.key)}
                    disabled={column.alwaysVisible}
                    label={
                      <>
                        {column.label}
                        {column.alwaysVisible && <span className={styles.columnRequired}> (Required)</span>}
                      </>
                    }
                  />
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnSelector;
